
import { RevModal } from '@ds/components/Modal'
import { closeModal, openModal } from '@ds/components/ModalBase'
import { mapGetters } from 'vuex'

import { MODAL_NAMES } from '@config/constants'
import { addSwapToCart } from '@http/endpoints'
import { ROUTES } from '@router'
import { trackSwapClick, trackSwapModal } from '@tracking/events'

import DeviceQuestions from './DeviceQuestions.vue'
import Error from './Error.vue'
import translations from './Modal.translations'
import NoOffer from './NoOffer.vue'
import SwapConfirmation from './SwapConfirmation.vue'
import SwapIntro from './SwapIntro.vue'

export const STEP = {
  INTRO: 'intro',
  DETAILS: 'details',
  CONFIRMATION: 'confirmation',
  NO_OFFER: 'noOffer',
  ERROR: 'error',
}

export default {
  components: {
    RevModal,
    SwapIntro,
    DeviceQuestions,
    SwapConfirmation,
    Error,
    NoOffer,
  },
  props: {
    price: {
      type: String,
      required: true,
    },
    productTitle: {
      type: String,
      required: true,
    },
    productPrice: {
      type: String,
      required: true,
    },
    delivery: {
      type: String,
      required: true,
    },
    showDelivery: {
      type: Boolean,
    },
    swapListings: {
      type: Array,
      default: () => [],
    },
    close: {
      type: String,
      default: 'none',
      validator: (value) =>
        [ROUTES.CHECKOUT.CART, ROUTES.PRODUCT.HOME, 'none'].includes(value),
    },
    payload: {
      type: Object,
      default: () => ({}),
    },
    datalayerCategory: {
      type: String,
      default: '',
    },
    modalName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      step: STEP.INTRO,
      answersDatalayerPayload: {},
      isEnabledDatalayer: true,
      confirmationWordings: [],
    }
  },

  computed: {
    translations: () => translations,
    STEP: () => STEP,
    ...mapGetters({
      questions: 'swap/parsedQuestions',
      hasNoOffer: 'swap/hasNoOffer',
      answers: 'swap/answers',
      offer: 'swap/offer',
      offerTitle: 'swap/offerTitle',
      offerPrice: 'swap/offerPrice',
      offerPriceWithCurrency: 'swap/offerPriceWithCurrency',
    }),
    closeWording() {
      switch (this.close) {
        case ROUTES.PRODUCT.HOME:
          return this.$t(translations.noFromProduct)

        case 'none':
          return this.$t(translations.noFromCart)

        default:
          return this.$t(translations.no)
      }
    },
  },

  methods: {
    async handleClose() {
      switch (this.close) {
        case ROUTES.CHECKOUT.CART:
          this.$emit('continue')
          break
        case ROUTES.PRODUCT.HOME:
          if (this.step === STEP.CONFIRMATION) {
            try {
              await this.$store.dispatch(
                'swap/getSwapEstimation',
                this.swapListings,
              )
            } catch {
              // fails silently
            }
          }
          break
        default:
      }

      if (this.step === STEP.CONFIRMATION) {
        this.$emit('confirmation')
      }

      if (this.isEnabledDatalayer) {
        this.handleTracking({ type: 'closeModal' })
      }
      this.step = STEP.INTRO
      this.$store.dispatch('swap/reset')
    },
    async handleAnswer({ key, value }) {
      const atIndex = this.answers.findIndex((item) => item.key === key)

      if (atIndex >= 0) {
        this.$store.dispatch('swap/resetAnswers', atIndex)
        // if user is re-filling a previous question, delete all the subsequents
        this.$store.dispatch('swap/resetQuestions', atIndex + 1)
        await this.$nextTick()
      }
      await this.$store.dispatch('swap/saveAnswer', { key, value })
      this.$store.dispatch('swap/setAnsweredQuestion', { key, value })

      this.answersDatalayerPayload[key] = value

      // TODO: We should never override event key!
      this.track({ event: 'swap', action: 'funnel > Step 2', label: key })
    },
    async handleCompleted({ to }) {
      this.$refs.scrollAnchor.scrollIntoView()
      try {
        switch (to) {
          case 'details':
            await this.$store.dispatch('swap/getQuestions')
            this.step = STEP.DETAILS
            break

          case 'confirmation':
            this.handleTracking({ type: 'seeEstimation' })
            try {
              const { wording } = await this.$store.dispatch('swap/postAnswers')

              this.confirmationWordings = wording ?? []
            } catch {
              // fails silently
            }

            if (this.hasNoOffer) {
              this.step = STEP.NO_OFFER

              return
            }

            this.step = STEP.CONFIRMATION
            break

          case 'close':
            this.$store.dispatch('swap/disableSwap')
            this.hideModal()
            break

          case ROUTES.CHECKOUT.CART:
            try {
              await this.$store.dispatch('http/request', {
                request: addSwapToCart,
                body: this.offer,
              })
            } catch (error) {
              // Fails silently
            }
            if (this.close === ROUTES.CHECKOUT.CART) {
              setTimeout(() => {
                openModal(MODAL_NAMES.PRODUCT.ADD_TO_CART)
              })
            }
            this.hideModal()
            break

          default:
        }
      } catch {
        this.step = STEP.ERROR
      }
    },
    async handleOpen() {
      this.isEnabledDatalayer = true
      this.track({
        action: 'modal',
        label: 'swap',
      })
    },
    track(args) {
      trackSwapModal({
        category: this.datalayerCategory,
        ...this.answersDatalayerPayload,
        ...args,
        swap_estimation: this.offerPrice,
      })

      trackSwapClick({
        value: {
          category: this.datalayerCategory,
          ...this.answersDatalayerPayload,
          swap_estimation: this.offerPrice,
        },
        name: args.label,
        zone: 'swap',
      })
    },
    handleTracking({ type }) {
      const closeTo =
        this.close === ROUTES.PRODUCT.HOME ? 'back_to_product' : 'go_to_cart'
      switch (type) {
        case 'closeIntro':
          this.track({
            action: 'funnel > Step 1',
            label: closeTo,
          })
          break

        case 'toEstimate':
          this.track({
            action: 'funnel > Step 1',
            label: 'buyback_estimate',
          })
          break

        case 'seeEstimation':
          this.track({
            action: 'funnel > Step 2',
            label: 'see_estimate',
          })
          break

        case 'acceptOffer':
          this.track({
            action: 'funnel > Step 3',
            label: 'accept_offer',
          })
          break

        case 'declineOffer':
          this.track({
            action: 'funnel > Step 3',
            label: 'decline_offer',
          })
          break

        case 'noOffer':
          this.track({
            action: 'funnel > Step 2',
            label: 'scarabee',
          })
          break

        case 'error':
          this.track({
            action: 'funnel',
            label: 'server_error',
          })
          break

        case 'closeModal':
          this.track({
            action: 'funnel',
            label: closeTo,
          })
          break

        default:
      }
    },
    hideModal() {
      this.isEnabledDatalayer = false
      closeModal(this.modalName)
    },
    goToCart() {
      this.hideModal()

      return this.$navigation.push({
        type: 'internal',
        name: ROUTES.CHECKOUT.CART,
      })
    },
  },
}
